<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon class="mr-2">mdi-account-multiple</v-icon>
            Manage Sales Representatives
          </v-card-title>
          <v-card-subtitle>
            Invite and manage your Sales Representatives
          </v-card-subtitle>

          <v-card-text>
            <!-- Invite New Sales Rep -->
            <v-card outlined class="mb-4">
              <v-card-title>Invite New Sales Representative</v-card-title>
              <v-card-text>
                <v-form ref="inviteForm" v-model="inviteValid">
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="inviteData.name"
                        label="Name"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="inviteData.email"
                        label="Email *"
                        type="email"
                        outlined
                        dense
                        :rules="[
                          v => !!v || 'Email is required',
                          v => /.+@.+\..+/.test(v) || 'Email must be valid'
                        ]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="inviteData.phone"
                        label="Phone"
                        outlined
                        dense
                        @input="formatPhone"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-btn
                    color="primary"
                    :loading="inviteLoading"
                    :disabled="!inviteValid"
                    @click="sendInvite"
                  >
                    <v-icon left>mdi-email-send</v-icon>
                    Send Invitation
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>

            <!-- List of Sales Representatives -->
            <v-card outlined>
              <v-card-title>
                Sales Representatives
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headers"
                  :items="salesReps"
                  :search="search"
                  :loading="loading"
                  loading-text="Loading Sales Representatives..."
                >
                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="getStatusColor(item.status)"
                      small
                      dark
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-slot:no-data>
                    <div class="text-center pa-4">
                      <p>No Sales Representatives found.</p>
                      <p class="text--secondary">Invite a Sales Representative to get started.</p>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Snackbar -->
    <v-snackbar v-model="successSnackbar" color="success" :timeout="5000">
      {{ successMessage }}
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
      {{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "GeneralAgentManagement",
  data() {
    return {
      loading: false,
      inviteLoading: false,
      inviteValid: false,
      search: "",
      successSnackbar: false,
      successMessage: "",
      errorSnackbar: false,
      errorMessage: "",
      inviteData: {
        name: "",
        email: "",
        phone: "",
      },
      salesReps: [],
      headers: [
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Phone", value: "phone" },
        { text: "Status", value: "status" },
      ],
      generalAgentId: null,
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    async getUser() {
      try {
        const response = await axios.get("/api/user");
        this.user = response.data[0];
        
        // Buscar General Agent pelo userID
        await this.findGeneralAgent();
      } catch (error) {
        console.error("Error getting user:", error);
        this.$router.push("/login");
      }
    },
    async findGeneralAgent() {
      try {
        // Buscar General Agent pelo userID
        const response = await axios.get(`/api/agent-enrollment/general-agent/by-userid/${this.user.userID}`);
        
        this.generalAgentId = response.data.id;
        
        // Buscar Sales Representatives
        const salesRepsResponse = await axios.get(`/api/agent-enrollment/general-agent/${this.generalAgentId}/sales-reps`);
        this.salesReps = salesRepsResponse.data.salesRepresentatives.map(rep => ({
          name: rep.name,
          email: rep.email,
          phone: rep.phone,
          status: rep.agentId ? 'Enrolled' : 'Pending'
        }));
      } catch (error) {
        console.error("Error finding General Agent:", error);
        if (error.response?.status === 404) {
          this.errorMessage = "General Agent not found. Please contact support.";
        } else {
          this.errorMessage = "Error loading General Agent information";
        }
        this.errorSnackbar = true;
      }
    },
    formatPhone() {
      let x = this.inviteData.phone
        .replace(/\D/g, "")
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      this.inviteData.phone = !x[2]
        ? x[1]
        : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    },
    async sendInvite() {
      if (!this.$refs.inviteForm.validate()) {
        return;
      }

      if (!this.generalAgentId) {
        this.errorMessage = "General Agent ID not found. Please contact support.";
        this.errorSnackbar = true;
        return;
      }

      this.inviteLoading = true;

      try {
        await axios.post(
          `/api/agent-enrollment/general-agent/${this.generalAgentId}/invite-sales-rep`,
          this.inviteData
        );

        this.successMessage = `Invitation sent successfully to ${this.inviteData.email}`;
        this.successSnackbar = true;

        // Reset form
        this.inviteData = {
          name: "",
          email: "",
          phone: "",
        };
        this.$refs.inviteForm.reset();

        // Refresh list
        await this.findGeneralAgent();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          "Error sending invitation. Please try again.";
        this.errorSnackbar = true;
      } finally {
        this.inviteLoading = false;
      }
    },
    getStatusColor(status) {
      const colors = {
        Enrolled: "success",
        Pending: "warning",
      };
      return colors[status] || "grey";
    },
  },
};
</script>

